export enum OrderRelationType {
  ROOT = 'ROOT',
  DESCENDANT = 'DESCENDANT',
}

export interface OrderUpdateContext {
  orderId: string;
  data: any;
  parentId?: string;
}

export interface OrderStrategy {
  updateOrderHandler(context: OrderUpdateContext, state: any): any;
  updateOrderItems(context: OrderUpdateContext, state: any): any;
  updateOrderEtas(context: OrderUpdateContext, state: any): any;
  updateOrderRemarks(context: OrderUpdateContext, state: any): any;
  updateOrderMetaInfo(context: OrderUpdateContext, state: any): any;
  setOrderItems(allOrders: any, state: any, parentId?: string): any;
  setOrderEtas(allOrders: any, state: any, parentId?: string): any;
  setOrderRemarks(allOrders: any, state: any, parentId?: string): any;
}

class RootOrderStrategy implements OrderStrategy {
  updateOrderHandler(context: OrderUpdateContext, state: any): any {
    const newAllOrders = state.allOrders.orders.map((item: any) => {
      if (item._id === context.orderId) {
        return {
          ...item,
          orderHandler: [context.data],
        };
      }
      return item;
    });

    return {
      ...state,
      allOrders: { ...state.allOrders, orders: newAllOrders },
    };
  }

  updateOrderItems(context: OrderUpdateContext, state: any): any {
    const newAllOrderItems = { ...state.orderItems };
    newAllOrderItems[context.orderId] = context.data;
    
    return { 
      ...state, 
      orderItems: { ...state.orderItems, ...newAllOrderItems }
    };
  }

  updateOrderEtas(context: OrderUpdateContext, state: any): any {
    const newAllOrderEtas = { ...state.orderEtas };
    newAllOrderEtas[context.orderId] = context.data;

    return { 
      ...state, 
      orderEtas: { ...state.orderEtas, ...newAllOrderEtas }
    };
  }

  updateOrderRemarks(context: OrderUpdateContext, state: any): any {
    const newAllOrderRemarks = { ...state.orderRemarks };
    newAllOrderRemarks[context.orderId] = context.data;
    
    return { 
      ...state, 
      orderRemarks: { ...state.orderRemarks, ...newAllOrderRemarks }
    };
  }

  updateOrderMetaInfo(context: OrderUpdateContext, state: any): any {
    const newAllOrders = state.allOrders.orders.map((item: any) => {
      if (item._id === context.orderId) {
        return {
          ...item,
          metaInfo: { ...(item.metaInfo || {}), ...context.data },
        };
      }
      return item;
    });

    return {
      ...state,
      allOrders: { ...state.allOrders, orders: newAllOrders },
    };
  }

  setOrderItems(allOrders: any, state: any): any {
    const newOrderItems: { [key: string]: any } = {};
    allOrders.orders.forEach((item: any) => {
      newOrderItems[item._id] = item.orderItems.filter(
        (item: any) => Object.entries(item).length !== 0
      );
    });
    return { ...state, orderItems: { ...state.orderItems, ...newOrderItems } };
  }

  setOrderEtas(allOrders: any, state: any): any {
    const newOrderEtas: { [key: string]: any } = {};
    allOrders.orders.forEach((item: any) => {
      newOrderEtas[item._id] = item.orderETA;
    });
    return { ...state, orderEtas: { ...state.orderEtas, ...newOrderEtas } };
  }

  setOrderRemarks(allOrders: any, state: any): any {
    const newOrderRemarks: { [key: string]: any } = {};
    allOrders.orders.forEach((item: any) => {
      newOrderRemarks[item._id] = item.orderRemarks;
    });
    return { ...state, orderRemarks: { ...state.orderRemarks, ...newOrderRemarks } };
  }
}

class ChildOrderStrategy implements OrderStrategy {
  updateOrderHandler(context: OrderUpdateContext, state: any): any {
    if (!context.parentId) {
      throw new Error('parentId is required for child order operations');
    }

    const existing = state.childOrders.get(context.parentId) ?? [];
    const newChildOrder = existing.map((item: any) => {
      if (item._id === context.orderId) {
        return {
          ...item,
          orderHandler: [context.data],
        };
      }
      return item;
    });

    return {
      ...state,
      childOrders: new Map(state.childOrders).set(context.parentId, newChildOrder),
    };
  }

  updateOrderItems(context: OrderUpdateContext, state: any): any {
    const newAllOrderItems = { ...state.orderItems };
    newAllOrderItems[context.orderId] = context.data;
    
    // Also update child orders if this orderId exists in any childOrders
    const newChildOrders = new Map(state.childOrders);
    for (const [parentId, childOrders] of Array.from(newChildOrders.entries())) {
      const updatedChildOrders = (childOrders as any[]).map((childOrder: any) => {
        if (childOrder._id === context.orderId) {
          return {
            ...childOrder,
            orderItems: context.data,
          };
        }
        return childOrder;
      });
      
      if (updatedChildOrders.some((child: any) => child._id === context.orderId)) {
        newChildOrders.set(parentId, updatedChildOrders);
      }
    }
    
    return { 
      ...state, 
      orderItems: { ...state.orderItems, ...newAllOrderItems },
      childOrders: newChildOrders
    };
  }

  updateOrderEtas(context: OrderUpdateContext, state: any): any {
    const newAllOrderEtas = { ...state.orderEtas };
    newAllOrderEtas[context.orderId] = context.data;

    // Also update child orders if this orderId exists in any childOrders
    const newChildOrders = new Map(state.childOrders);
    for (const [parentId, childOrders] of Array.from(newChildOrders.entries())) {
      const updatedChildOrders = (childOrders as any[]).map((childOrder: any) => {
        if (childOrder._id === context.orderId) {
          return {
            ...childOrder,
            orderETA: context.data,
          };
        }
        return childOrder;
      });
      
      if (updatedChildOrders.some((child: any) => child._id === context.orderId)) {
        newChildOrders.set(parentId, updatedChildOrders);
      }
    }

    return { 
      ...state, 
      orderEtas: { ...state.orderEtas, ...newAllOrderEtas },
      childOrders: newChildOrders
    };
  }

  updateOrderRemarks(context: OrderUpdateContext, state: any): any {
    const newAllOrderRemarks = { ...state.orderRemarks };
    newAllOrderRemarks[context.orderId] = context.data;
    
    // Also update child orders if this orderId exists in any childOrders
    const newChildOrders = new Map(state.childOrders);
    for (const [parentId, childOrders] of Array.from(newChildOrders.entries())) {
      const updatedChildOrders = (childOrders as any[]).map((childOrder: any) => {
        if (childOrder._id === context.orderId) {
          return {
            ...childOrder,
            orderRemarks: context.data,
          };
        }
        return childOrder;
      });
      
      if (updatedChildOrders.some((child: any) => child._id === context.orderId)) {
        newChildOrders.set(parentId, updatedChildOrders);
      }
    }
    
    return { 
      ...state, 
      orderRemarks: { ...state.orderRemarks, ...newAllOrderRemarks },
      childOrders: newChildOrders
    };
  }

  updateOrderMetaInfo(context: OrderUpdateContext, state: any): any {
    const newAllOrders = state.allOrders.orders.map((item: any) => {
      if (item._id === context.orderId) {
        return {
          ...item,
          metaInfo: { ...(item.metaInfo || {}), ...context.data },
        };
      }
      return item;
    });

    // Also update child orders if this orderId exists in any childOrders
    const newChildOrders = new Map(state.childOrders);
    for (const [parentId, childOrders] of Array.from(newChildOrders.entries())) {
      const updatedChildOrders = (childOrders as any[]).map((childOrder: any) => {
        if (childOrder._id === context.orderId) {
          return {
            ...childOrder,
            metaInfo: { ...(childOrder.metaInfo || {}), ...context.data },
          };
        }
        return childOrder;
      });
      
      if (updatedChildOrders.some((child: any) => child._id === context.orderId)) {
        newChildOrders.set(parentId, updatedChildOrders);
      }
    }

    return {
      ...state,
      allOrders: { ...state.allOrders, orders: newAllOrders },
      childOrders: newChildOrders,
    };
  }

  setOrderItems(allOrders: any, state: any, parentId?: string): any {
    const newOrderItems: { [key: string]: any } = {};
    if (parentId) {
      allOrders.forEach((item: any) => {
        newOrderItems[item._id] = item.orderItems.filter(
          (item: any) => Object.entries(item).length !== 0
        );
      });
      return { ...state, orderItems: { ...state.orderItems, ...newOrderItems } };
    }
    return state;
  }

  setOrderEtas(allOrders: any, state: any, parentId?: string): any {
    const newOrderEtas: { [key: string]: any } = {};
    if (parentId) {
      allOrders.forEach((item: any) => {
        newOrderEtas[item._id] = item.orderETA;
      });
      return { ...state, orderEtas: { ...state.orderEtas, ...newOrderEtas } };
    }
    return state;
  }

  setOrderRemarks(allOrders: any, state: any, parentId?: string): any {
    const newOrderRemarks: { [key: string]: any } = {};
    if (parentId) {
      allOrders.forEach((item: any) => {
        newOrderRemarks[item._id] = item.orderRemarks;
      });
      return { ...state, orderRemarks: { ...state.orderRemarks, ...newOrderRemarks } };
    }
    return state;
  }
}

export const ORDER_STRATEGIES: Record<OrderRelationType, OrderStrategy> = {
  [OrderRelationType.ROOT]: new RootOrderStrategy(),
  [OrderRelationType.DESCENDANT]: new ChildOrderStrategy(),
};